import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useMemo } from 'react';
import { pick } from 'lodash';
import { useQuery } from 'react-query';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import sortBy from 'lodash/sortBy';
import { UseFormMethods } from 'react-hook-form';
import { useFetchConfiguration } from 'components/app/use-app';
import { Person } from '../../global/entities/Person';
import { RootState } from '../../global';
import { buyOrderPeopleSelectors } from '../admin/buy-process/people/people-redux';
import { useStepper } from '../../utils/use-stepper';
import {
  fetchClosingDatesOptions,
  fetchMunicipalities,
} from './buy-process-form-api';
import { useBuyProcessForm } from './buy-process-form-utils';
import { fiscalYearOptions } from './components/fiscal-year/fiscal-year-utils';
import { BuyProcessUtils } from './index';

export function useSelectPeople(id: string): Person[] {
  const people = useSelector((state: RootState) =>
    buyOrderPeopleSelectors.selectAll(state.buyOrderPeople[id]));

  return people.filter(
    (person) =>
      person.socialSecurityNumber != null && person.socialSecurityNumber !== '',
  );
}

export function useReCalculateExtraServices(
  activeStep: number,
  orderId: string,
) {
  const {
    nameReview,
    taxHelp,
    beneficialOwnerHelp: beneficial,
  } = useSelector(BuyProcessUtils.selectExtraServices(orderId));

  const fetchExtraServices = BuyProcessUtils.useFetchExtraServicesCost({
    nameReview,
    beneficial,
    taxHelp,
  });

  const { refetch } = fetchExtraServices;
  useEffect(() => {
    refetch();
  }, [refetch, activeStep, beneficial, nameReview, taxHelp]);
}

export const QUERY_KEY_MUNICIPALITIES = 'municipalities';
export function useFetchMunicipalities() {
  const { data, ...rest } = useQuery(
    QUERY_KEY_MUNICIPALITIES,
    fetchMunicipalities,
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    },
  );

  return { municipalities: data ?? [], ...rest };
}

export const QUERY_KEY_CLOSING_DATE = 'closing-date-options';
export function useFetchClosingDateOptions() {
  const { data, ...rest } = useQuery(
    QUERY_KEY_CLOSING_DATE,
    fetchClosingDatesOptions,
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    },
  );

  return { closingDateOptions: data ?? {}, ...rest };
}

export function useSaveBuyOrderFormState(
  action: ActionCreatorWithPayload<any>,
  values: Record<string, unknown>,
  validate: () => Promise<boolean>,
) {
  const dispatch = useDispatch();
  const { id, goToRequestedStep } = useBuyProcessForm();

  const $values = React.useRef(values);
  React.useEffect(() => {
    $values.current = values;
  });

  React.useEffect(() => {
    goToRequestedStep(validate, () => {
      dispatch(action({ id, ...$values.current }));
    });
  }, [action, dispatch, goToRequestedStep, id, validate]);
}

export function useBuyProcessFormSetup(activeStep: number, orderId: string) {
  const isVerticalStepper = BuyProcessUtils.useIsVerticalStepper();
  useReCalculateExtraServices(activeStep, orderId);
  useFetchConfiguration();
  const { municipalities } = useFetchMunicipalities();
  const { closingDateOptions } = useFetchClosingDateOptions();

  const municipalitiesMemoized = useMemo(
    () => sortBy(municipalities, (x) => x.value),
    [municipalities],
  );

  const closingDateOptionsMemoized = useMemo(
    () => pick(closingDateOptions, fiscalYearOptions),
    [closingDateOptions],
  );

  return {
    municipalities: municipalitiesMemoized,
    closingDateOptions: closingDateOptionsMemoized,
    isVerticalStepper,
  };
}

export async function onGoBackInForm(
  setRequestedStep: ReturnType<typeof useStepper>['setRequestedStep'],
  isValid: UseFormMethods['trigger'],
  event: React.MouseEvent<HTMLButtonElement>,
) {
  setRequestedStep((s) => s - 1);
  event.stopPropagation();
  if (!(await isValid())) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
}

export async function onGoForwardInForm(
  setRequestedStep: ReturnType<typeof useStepper>['setRequestedStep'],
  event: React.MouseEvent<HTMLButtonElement>,
) {
  setRequestedStep((s) => s + 1);
  event.stopPropagation();
}
